import React from "react";
import { useTranslation } from "react-i18next";
import type { Patient } from "../../domain/entities/Patient";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import SearchOff from "@/assets/search-off.svg?react";
import { PermissionGuard } from "./PermissionGuard";
import {
  desktopPaginatedTableShellClass,
  desktopScrollTableClass,
} from "@/components/layout/tableLayout";

interface PatientsTableProps {
  patients: Patient[];
  onView: (patient: Patient) => void;
  isLoading?: boolean;
  canView?: boolean;
}

export const PatientsTable: React.FC<PatientsTableProps> = ({
  patients,
  onView,
  isLoading,
  canView = true,
}) => {
  const { t } = useTranslation("patients");
  const hasLanguage = patients.some((patient) => patient.language);

  const handleRowClick = (patient: Patient) => {
    if (!canView) return;
    onView(patient);
  };

  const stopRowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div className="mt-4">
      <div className="space-y-3 md:hidden">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-xl border bg-white p-4">
              <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200" />
              <div className="mt-3 h-3 w-full animate-pulse rounded bg-gray-100" />
              <div className="mt-2 h-3 w-1/2 animate-pulse rounded bg-gray-100" />
            </div>
          ))
        ) : patients.length === 0 ? (
          <div className="rounded-xl border bg-white px-4 py-10 text-center">
            <SearchOff className="mx-auto mb-2 text-gray-400" />
            <p className="font-medium text-gray-700">{t("table.noResultsTitle")}</p>
            <p className="mt-1 text-sm text-gray-500">
              {t("table.noResultsBodyMobile")}
            </p>
          </div>
        ) : (
          patients.map((patient) => (
            <div
              key={patient.id}
              className={`rounded-xl border bg-white p-4 ${
                canView ? "cursor-pointer transition-colors hover:bg-gray-50" : ""
              }`}
              onClick={() => handleRowClick(patient)}
              onKeyDown={(event) => {
                if (!canView) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleRowClick(patient);
                }
              }}
              role={canView ? "button" : undefined}
              tabIndex={canView ? 0 : undefined}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F2F5FF] text-xs font-bold uppercase">
                    {patient.initials}
                  </span>
                  <div className="min-w-0">
                    <p
                      className="truncate font-medium text-gray-900"
                      title={patient.name.length > 20 ? patient.name : undefined}
                    >
                      {patient.name}
                    </p>
                    <p className="text-sm text-gray-500">{patient.phone}</p>
                  </div>
                </div>
                <PermissionGuard allowed={canView}>
                  <button
                    type="button"
                    onClick={(event) => {
                      stopRowClick(event);
                      onView(patient);
                    }}
                    className="app-text-button shrink-0"
                  >
                    {t("table.view")}
                  </button>
                </PermissionGuard>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs uppercase text-gray-400">{t("table.gender")}</p>
                  <p className="mt-1 text-gray-700">{patient.gender}</p>
                </div>
                {patient.language && (
                  <div className="min-w-0">
                    <p className="text-xs uppercase text-gray-400">{t("table.language")}</p>
                    <p
                      className="mt-1 truncate text-gray-700"
                      title={
                        patient.language.length > 20
                          ? patient.language
                          : undefined
                      }
                    >
                      {patient.language}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs uppercase text-gray-400">{t("table.created")}</p>
                  <p className="mt-1 text-gray-700">
                    {patient.formattedCreatedAt}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs uppercase text-gray-400">{t("table.ethnicity")}</p>
                  <p className="mt-1 text-gray-700">{patient.ethnicity}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className={desktopPaginatedTableShellClass}>
        <Table
          className={`${desktopScrollTableClass} ${
            hasLanguage ? "min-w-[760px]" : "min-w-[650px]"
          }`}
        >
          <TableHeader>
            <TableRow className="bg-[#D8DDE6] hover:bg-[#D8DDE6]">
              <TableHead className="py-3 px-4 font-semibold text-gray-700">
                {t("table.name")}
              </TableHead>
              <TableHead className="py-3 px-4 font-semibold text-gray-700">
                {t("table.phone")}
              </TableHead>
              <TableHead className="py-3 px-4 font-semibold text-gray-700">
                {t("table.gender")}
              </TableHead>
              {hasLanguage && (
                <TableHead className="py-3 px-4 font-semibold text-gray-700">
                  {t("table.language")}
                </TableHead>
              )}
              <TableHead className="py-3 px-4 font-semibold text-gray-700">
                {t("table.ethnicity")}
              </TableHead>
              <TableHead className="py-3 px-4 font-semibold text-gray-700">
                {t("table.createdOn")}
              </TableHead>
              <TableHead className="py-3 px-4 text-center font-semibold text-gray-700">
                {t("table.actions")}
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={hasLanguage ? 7 : 6} className="py-6">
                    <div className="h-4 bg-gray-200 animate-pulse rounded" />
                  </TableCell>
                </TableRow>
              ))
            ) : patients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={hasLanguage ? 7 : 6} className="py-12">
                  <div className="flex flex-col items-center justify-center text-center w-full">
                    <SearchOff className="mb-2 text-gray-400" />
                    <p className="font-medium text-gray-700">
                      {t("table.noResultsTitle")}
                    </p>
                    <p className="text-sm text-gray-500 w-72 text-center">
                      {t("table.noResultsBodyDesktopLine1")} <br />
                      {t("table.noResultsBodyDesktopLine2")}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              patients.map((patient) => (
                <TableRow
                  key={patient.id}
                  onClick={() => handleRowClick(patient)}
                  className={
                    canView ? "cursor-pointer hover:bg-gray-50" : "hover:bg-gray-50"
                  }
                >
                  <TableCell className="py-3 px-4 font-medium max-w-[180px]">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-[#F2F5FF] flex items-center justify-center text-xs font-bold uppercase shrink-0">
                        {patient.initials}
                      </span>
                      <span
                        className="block min-w-0 truncate"
                        title={
                          patient.name.length > 20 ? patient.name : undefined
                        }
                      >
                        {patient.name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 px-4">{patient.phone}</TableCell>
                  <TableCell className="py-3 px-4">{patient.gender}</TableCell>
                  {hasLanguage && (
                    <TableCell className="py-3 px-4 max-w-[160px]">
                      <span
                        className="truncate block"
                        title={
                          patient.language && patient.language.length > 20
                            ? patient.language
                            : undefined
                        }
                      >
                        {patient.language || "-"}
                      </span>
                    </TableCell>
                  )}
                  <TableCell className="py-3 px-4 max-w-[160px]">
                    <span
                      className="truncate block"
                      title={
                        patient.ethnicity && patient.ethnicity.length > 20
                          ? patient.ethnicity
                          : undefined
                      }
                    >
                      {patient.ethnicity}
                    </span>
                  </TableCell>
                  <TableCell className="py-3 px-4">
                    {patient.formattedCreatedAt}
                  </TableCell>
                  <TableCell
                    className="py-3 px-4 text-center"
                    onClick={stopRowClick}
                  >
                    <div className="flex items-center justify-center">
                      <PermissionGuard allowed={canView}>
                        <button
                          type="button"
                          className="app-text-button"
                          onClick={() => onView(patient)}
                        >
                          {t("table.view")}
                        </button>
                      </PermissionGuard>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
